import { useCallback, type ReactElement } from 'react';
import type { ParamValue } from '@gr/core/platform/config/registry';
import type { PlatformSettingsDto, SaveSettingResultDto } from '@gr/core/platform/config/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { PlatformSettings, type SettingOutcome } from './PlatformSettings/PlatformSettings';

// CFG-UC-2, CFG-RULE-1, CFG-UC-2.CC-1, CFG-UC-2.CC-2, CFG-UC-2.CC-3, CFG-UC-2.CC-4, DEMO-RULE-4
export function AdminSettingsPage(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const settings = useResource(() => api.config.settings(), [api]);

  const { setData } = settings;

  /** Ответ сервера — новое состояние параметра: подставить в список без перезагрузки. */
  const apply = useCallback(
    (result: OperationResult<SaveSettingResultDto>): SettingOutcome => {
      if (!result.success || !result.data) return { ok: false, errors: result.messages };
      const { setting, warnings } = result.data;
      setData((current) =>
        current ? ({ ...current, settings: current.settings.map((item) => (item.key === setting.key ? setting : item)) } satisfies PlatformSettingsDto) : current,
      );
      return { ok: true, warnings };
    },
    [setData],
  );
  // Колбэки стабильны: строки параметров (memo) не перерисовываются при сохранении соседних.
  const save = useCallback(async (key: string, value: ParamValue) => apply(await api.config.saveSetting(key, value)), [api, apply]);
  const reset = useCallback(async (key: string) => apply(await api.config.resetSetting(key)), [api, apply]);

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={t('config.title')}
        subtitle={t('config.subtitle')}
        actions={
          <Button icon="search" loading={settings.loading} onClick={() => void settings.reload()}>
            {t('admin.overview.refresh')}
          </Button>
        }
      />
      {settings.loading && !settings.data ? (
        <Loading label={t('common.loading')} />
      ) : settings.error || !settings.data ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          {te(settings.error)}
        </Alert>
      ) : (
        <PlatformSettings
          settings={settings.data.settings}
          readOnly={settings.data.readOnly}
          onSave={save}
          onReset={reset}
        />
      )}
    </div>
  );
}
